import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { FinanceService } from './services/finance.service';
import { FinanceStatisticsDto } from './models/finance-statistics-dto';
import { ContributionDto, SearchContributionDto } from './models/contribution-dto';
import { Observable } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { ApiResponse } from '../../shared/models/api-response.model';
import { SharedModule } from '../../shared/shared.module';
import { ExportType } from '../../shared/models/search-base';
import { FileHelper } from '../../shared/services/file-helper';

@Component({
  selector: 'app-finance',
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule, SharedModule],
  providers: [FileHelper],
  templateUrl: './finance.component.html',
  styleUrls: ['./finance.component.scss']
})
export class FinanceComponent implements OnInit {
  financeStatistics$!: Observable<ApiResponse<FinanceStatisticsDto>>;
  financeStatistics: FinanceStatisticsDto | null = null;
  selectedYear: string | null = '';

  // Contributions
  contributions: ContributionDto[] = [];
  searchDto: SearchContributionDto = {
    pageNumber: 1,
    pageSize: 10
  };
  isLoadingContributions = false;
  totalContributions = 0;

  // Filters
  searchText = '';
  selectedCategory = '';
  selectedStatus = '';
  selectedType = 'all';

  exportFileName: string = 'contributions';

  constructor(
    public translate: TranslateService,
    private financeService: FinanceService,
    private fileHelper: FileHelper
  ) {}

  ngOnInit(): void {
    this.loadFinanceStatistics();
    this.loadContributions();
  }

  onYearChange(): void {
    this.loadFinanceStatistics();
  }

  private loadFinanceStatistics(): void {
    if (this.selectedYear === '' || this.selectedYear === null) {
      // Load all years statistics
      this.financeStatistics$ = this.financeService.getFinanceStatistics();
    } else {
      // Load specific year statistics
      const yearNum = parseInt(this.selectedYear);
      this.financeStatistics$ = this.financeService.getFinanceStatisticsByYear(yearNum);
    }

    this.financeStatistics$.subscribe({
      next: (data) => {
        this.financeStatistics = data.value;
      },
      error: (error) => {
        console.error('Error loading finance statistics:', error);
      }
    });
  }

  loadContributions(): void {
    this.isLoadingContributions = true;
    this.searchDto.searchText = this.searchText;
    this.searchDto.category = this.selectedCategory;
    this.searchDto.status = this.selectedStatus;
    this.searchDto.type = this.selectedType;

    this.financeService.getContributions(this.searchDto).subscribe({
      next: (response) => {
        if (response.value) {
          this.contributions = response.value.items || [];
          this.totalContributions = response.value.totalItemsCount || 0;
        }
        this.isLoadingContributions = false;
      },
      error: (error) => {
        console.error('Error loading contributions:', error);
        this.contributions = [];
        this.totalContributions = 0;
        this.isLoadingContributions = false;
      }
    });
  }

  onSearchChange(): void {
    this.searchDto.pageNumber = 1; // Reset to first page
    this.loadContributions();
  }

  onCategoryChange(): void {
    this.searchDto.pageNumber = 1;
    this.loadContributions();
  }

  onStatusChange(): void {
    this.searchDto.pageNumber = 1;
    this.loadContributions();
  }

  onTypeFilterChange(type: string): void {
    this.selectedType = type;
    this.searchDto.pageNumber = 1;
    this.loadContributions();
  }

  onPageChange(page: number): void {
    this.searchDto.pageNumber = page;
    this.loadContributions();
  }

  onPagedData(event: any): void {
    this.searchDto.pageNumber = event.pageNumber;
    this.searchDto.pageSize = event.pageSize;
    this.loadContributions();
  }

  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'completed': return 'success';
      case 'partial': return 'warning';
      case 'pending': return 'danger';
      default: return 'secondary';
    }
  }

  getProgressBarClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'completed': return 'success';
      default: return '';
    }
  }

  onExportAsExcelClick(): void {
    this.export(ExportType.Xlsx, this.exportFileName + '.xlsx');
  }

  export(exportType: ExportType, fileName: string): void {
    // Clear items array before sending request to avoid validation errors
    const exportDto: SearchContributionDto = { ...this.searchDto, items: undefined };
    exportDto.exportTypeId = exportType;
    exportDto.isExport = true;

    this.financeService.exportContributions(exportDto).subscribe({
      next: (res) => {
        this.fileHelper.downloadFile(res, fileName);
      },
      error: (error: any) => this.fileHelper.handleError(error)
    });
  }
}
